import classNames from "classnames";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SliderMultipleValue } from "../slider/slider-multiple-value";
import { SliderMultipleValueObject } from "../slider/slider-multiple-value.types";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";
import {
  getMaxTimeByStep,
  getTimeDisplayText,
  getValidTimeAsString,
  getValidTimeInput24HR,
  minutesTo24HrTime,
  twentyFourHrToMinutes,
} from "./time-selection.util";
import { TimeTextInput } from "./time-text-input";
import { TimeRangeSliderProps } from "./types/time-range-slider.types";

export const TimeRangeSlider = ({
  label,
  startTime: startTimeInput,
  endTime: endTimeInput,
  startTimeLabel = "Please enter a time",
  endTimeLabel = "Please enter a time",
  defaultStartTime = "00:00",
  defaultEndTime,
  minStartTime = "00:00",
  maxStartTime,
  minEndTime,
  maxEndTime,
  timeFormat,
  step = 60,
  disabled,
  onChange,
  hourAriaLabel = "Please enter hour",
  minuteAriaLabel = "Please enter minute",
  periodAriaLabel = "Please enter a.m. or p.m.",
  startTitle = "Start",
  endTitle = "End",
  startButtonAriaLabel = "Set start time",
  endButtonAriaLabel = "Set end time",
  startInputFieldId,
  endInputFieldId,
}: TimeRangeSliderProps): ReactElement => {
  useSSR(() => {
    isPropEmpty(startInputFieldId) &&
      showSSRHydrationWarning("startInputFieldId", "TimeRangeSlider");
    isPropEmpty(endInputFieldId) &&
      showSSRHydrationWarning("endInputFieldId", "TimeRangeSlider");
  });

  const maxTimeByStep = getMaxTimeByStep(step);
  defaultEndTime = defaultEndTime || maxTimeByStep;
  maxEndTime = maxEndTime || maxTimeByStep;
  maxStartTime =
    maxStartTime || minutesTo24HrTime(twentyFourHrToMinutes(maxEndTime) - step);
  minEndTime =
    minEndTime || minutesTo24HrTime(twentyFourHrToMinutes(minStartTime) + step);

  const isControlled =
    endTimeInput !== undefined && startTimeInput !== undefined;

  const startTimeInMinutes = useRef(0);
  const endTimeInMinutes = useRef(0);

  const sliderMinValue = twentyFourHrToMinutes(minStartTime);
  const sliderMaxValue = twentyFourHrToMinutes(maxEndTime);

  const [_startTime, setStartTime] = useState(() => {
    const timeInput = getValidTimeAsString({
      timeFormat,
      isControlled,
      timeInput: startTimeInput,
      defaultTime: defaultStartTime,
    });
    startTimeInMinutes.current = twentyFourHrToMinutes(timeInput);
    return timeInput;
  });

  const [_endTime, setEndTime] = useState(() => {
    const timeInput = getValidTimeAsString({
      timeFormat,
      isControlled,
      timeInput: endTimeInput,
      defaultTime: defaultEndTime,
    });
    endTimeInMinutes.current = twentyFourHrToMinutes(timeInput);
    return timeInput;
  });

  const getMinMaxBoundTime = (
    timeInMins: number,
    // time in 24 hour format
    min?: string,
    // time in 24 hour format
    max?: string
  ): number => {
    if (min) {
      const minValInMinutes = twentyFourHrToMinutes(min);
      timeInMins = timeInMins < minValInMinutes ? minValInMinutes : timeInMins;
    }
    if (max) {
      const maxValInMinutes = twentyFourHrToMinutes(max);
      timeInMins = timeInMins > maxValInMinutes ? maxValInMinutes : timeInMins;
    }
    return timeInMins;
  };

  const getMinsRoundByStep = (mins: number, step: number): number =>
    Math.round(mins / step) * step;

  const setValidStartTime = useCallback(
    (timeInput: string, isOnInputChange?: boolean): void => {
      let timeInMins = twentyFourHrToMinutes(timeInput);
      timeInMins = getMinsRoundByStep(timeInMins, step);

      timeInMins = getMinMaxBoundTime(timeInMins, minStartTime, maxStartTime);
      if (timeInMins >= endTimeInMinutes.current) {
        timeInMins = endTimeInMinutes.current - step;
      }
      startTimeInMinutes.current = timeInMins;
      const newStartTime = minutesTo24HrTime(timeInMins);
      if (_startTime !== newStartTime) {
        setStartTime(newStartTime);
        if (isOnInputChange) {
          onChange?.(newStartTime, _endTime);
        }
      }
    },
    [step, minStartTime, maxStartTime, _startTime, onChange, _endTime]
  );

  const setValidEndTime = useCallback(
    (timeInput: string, isOnInputChange?: boolean): void => {
      let timeInMins = twentyFourHrToMinutes(timeInput);
      timeInMins = getMinsRoundByStep(timeInMins, step);

      timeInMins = getMinMaxBoundTime(timeInMins, minEndTime, maxEndTime);
      if (timeInMins <= startTimeInMinutes.current) {
        timeInMins = startTimeInMinutes.current + step;
      }
      endTimeInMinutes.current = timeInMins;
      const newEndTime = minutesTo24HrTime(timeInMins);
      if (_endTime !== newEndTime) {
        setEndTime(newEndTime);
        if (isOnInputChange) {
          onChange?.(_startTime, newEndTime);
        }
      }
    },
    [_endTime, _startTime, maxEndTime, minEndTime, onChange, step]
  );

  useEffect(() => {
    if (isControlled) {
      setValidStartTime(
        startTimeInput ? getValidTimeInput24HR(startTimeInput, timeFormat) : ""
      );
    }
  }, [isControlled, startTimeInput, setValidStartTime, timeFormat]);

  useEffect(() => {
    if (isControlled) {
      setValidEndTime(
        endTimeInput ? getValidTimeInput24HR(endTimeInput, timeFormat) : ""
      );
    }
  }, [isControlled, endTimeInput, setValidEndTime, timeFormat]);

  const renderStartTimeInput = (id: string): ReactElement => {
    const onStartTimeChange = (startTimeInput: string): void => {
      if (startTimeInput === "") {
        startTimeInput = minStartTime;
      }
      setValidStartTime(startTimeInput, true);
    };

    return (
      <TimeTextInput
        label={startTimeLabel}
        timeFormat={timeFormat}
        hourAriaLabel={hourAriaLabel}
        minuteAriaLabel={minuteAriaLabel}
        periodAriaLabel={periodAriaLabel}
        onChange={onStartTimeChange}
        value={_startTime}
        disabled={disabled}
        inputAttrs={{
          id,
          "data-range": "min",
          step: step.toString(),
          title: startTitle,
          min: minStartTime || "",
          max: maxStartTime || "",
        }}
      />
    );
  };

  const renderEndTimeInput = (id: string): ReactElement => {
    const onEndTimeChange = (endTimeInput: string): void => {
      if (endTimeInput === "") {
        endTimeInput = maxEndTime || maxTimeByStep;
      }
      setValidEndTime(endTimeInput, true);
    };

    return (
      <TimeTextInput
        label={endTimeLabel}
        timeFormat={timeFormat}
        hourAriaLabel={hourAriaLabel}
        minuteAriaLabel={minuteAriaLabel}
        periodAriaLabel={periodAriaLabel}
        onChange={onEndTimeChange}
        value={_endTime}
        disabled={disabled}
        inputAttrs={{
          id,
          "data-range": "max",
          step: step.toString(),
          title: endTitle,
          min: minEndTime || "",
          max: maxEndTime || "",
        }}
      />
    );
  };

  const sliderOnChange = ({
    min: _start,
    max: _end,
  }: SliderMultipleValueObject): void => {
    const start = getMinMaxBoundTime(_start, minStartTime, maxStartTime);
    const end = getMinMaxBoundTime(_end, minEndTime, maxEndTime);
    const startTimeVal = minutesTo24HrTime(start);
    const endTimeVal = minutesTo24HrTime(end);
    if (
      start !== startTimeInMinutes.current ||
      end !== endTimeInMinutes.current
    ) {
      startTimeInMinutes.current = start;
      endTimeInMinutes.current = end;
      setStartTime(startTimeVal);
      setEndTime(endTimeVal);
      onChange?.(startTimeVal, endTimeVal);
    }
  };

  const timeSliderClass = classNames("spark-time-range-slider", {
    "all-disabled": disabled,
  });

  const startTimeDisplayText = getTimeDisplayText(_startTime, timeFormat);
  const endTimeDisplayText = getTimeDisplayText(_endTime, timeFormat);

  return (
    <SliderMultipleValue
      label={label}
      minRange={sliderMinValue}
      maxRange={sliderMaxValue}
      startTitle={startTitle}
      endTitle={endTitle}
      step={step.toString()}
      minValue={startTimeInMinutes.current}
      maxValue={endTimeInMinutes.current}
      startInputFieldId={startInputFieldId}
      endInputFieldId={endInputFieldId}
      _timeRangePrivateProps={{
        timeSliderClassName: timeSliderClass,
        timeSliderInputSeparatorClassName:
          "spark-time-range-slider__input-divider",
        renderMinInput: renderStartTimeInput,
        renderMaxInput: renderEndTimeInput,
        minCeilValue: maxStartTime
          ? twentyFourHrToMinutes(maxStartTime)
          : undefined,
        maxFloorValue: minEndTime
          ? twentyFourHrToMinutes(minEndTime)
          : undefined,
        startButtonProps: {
          "aria-valuemax": Math.min(
            endTimeInMinutes.current - step,
            twentyFourHrToMinutes(maxStartTime)
          ),
          "aria-valuetext": startTimeDisplayText,
          "data-value": startTimeDisplayText,
          "data-value-minutes": startTimeInMinutes.current,
          "aria-label": startButtonAriaLabel,
        },
        endButtonProps: {
          "aria-valuemin": Math.max(
            startTimeInMinutes.current + step,
            twentyFourHrToMinutes(minEndTime)
          ),
          "aria-valuetext": endTimeDisplayText,
          "data-value": endTimeDisplayText,
          "data-value-minutes": twentyFourHrToMinutes(_endTime),
          "aria-label": endButtonAriaLabel,
        },
      }}
      onChange={sliderOnChange}
      disabled={disabled}
    />
  );
};

TimeRangeSlider.displayName = "TimeRangeSlider";
